import React from "react";
import shouldGroupComponents from "./shouldGroupComponents";
import Cards from "./components/Cards";
import Partnership from "./components/Partnership";
import Quote from "./components/Quote";
import SolutionArea from "./components/SolutionArea";
import NextBestAction from "./components/NextBestAction";
import FAQs from "./components/FAQs";
import Statistics from "./components/Statistics";
import MarqueeTitleSection from "./components/MarqueeTitleSection";
import BigImage from "./components/BigImage";
import Subnavigation from "./components/Subnavigation";
import ScrollableContent from "./components/ScrollableContent";
import Leadership from "./components/Leadership";
import Hero from "./components/Hero";
import ClientLogos from "./components/ClientLogos";
import Jumbotron from "./components/Jumbotron";
import ContentArea from "./components/ContentArea";
import ImpactStorySummary from "./components/ImpactStorySummary";
import ComponentGroup from "./components/ComponentGroup";
import ComponentGroupHeading from "./components/ComponentGroupHeading";
import Paragraph from "./components/Paragraph";
import ImpactStoryJumbotron from "./components/ImpactStoryJumbotron";
import RichTextArea from "./components/RichTextArea";
import PartnershipLogos from "./components/PartnershipLogos";
import Cta from "./components/CTA";
import ContactUsForm from "./components/ContactUsForm";

const getContentPageComponents = (
  components,
  hasGroupedComponents,
  location,
  onChangeInView,
  inViewItem
) => {
  const componentsToRender = [];
  const componentsToNotRender = [];
  if (!components) return componentsToRender;

  components
    .filter((component) => !!component.id)
    .forEach((component, i) => {
      if (component.__typename === "ContentfulHero") {
        componentsToRender.push(<Hero key={component.id} {...component} />);
      }

      if (component.__typename === "ContentfulSubnavigation") {
        componentsToRender.push(
          <Subnavigation
            key={component.id}
            slug={location.pathname}
            inViewItem={inViewItem}
            {...component}
          />
        );
      }

      if (component.__typename === "ContentfulClientLogos") {
        componentsToRender.push(
          <ClientLogos key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulPartnership") {
        componentsToRender.push(
          <Partnership key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulPartnershipLogos") {
        componentsToRender.push(
          <PartnershipLogos key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulQuote") {
        componentsToRender.push(<Quote key={component.id} {...component} />);
      }

      if (component.__typename === "ContentfulSolutionArea") {
        componentsToRender.push(
          <SolutionArea
            key={component.id}
            onChangeInView={onChangeInView}
            {...component}
          />
        );
      }

      if (component.__typename === "ContentfulCards") {
        componentsToRender.push(<Cards key={component.id} {...component} />);
      }

      if (component.__typename === "ContentfulFaQs") {
        componentsToRender.push(<FAQs key={component.id} {...component} />);
      }

      if (component.__typename === "ContentfulStatistics") {
        componentsToRender.push(
          <Statistics key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulMarqueeTitleSection") {
        componentsToRender.push(
          <MarqueeTitleSection key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulNextBestAction") {
        componentsToRender.push(
          <NextBestAction key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulBigImage") {
        componentsToRender.push(<BigImage key={component.id} {...component} />);
      }

      if (component.__typename === "ContentfulScrollableContent") {
        componentsToRender.push(
          <ScrollableContent key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulLeadership") {
        componentsToRender.push(
          <Leadership key={component.id} {...component} />
        );
      }

      /**
       * Some content area components should be grouped with their next component,
       * to support sticky positioning or any other kind shared state
       */
      if (component.__typename === "ContentfulContentArea") {
        const nextComponent = components[i + 1];
        const shouldGroup =
          nextComponent &&
          shouldGroupComponents(
            component,
            nextComponent,
            location?.pathname === "/"
          );
        if (shouldGroup && hasGroupedComponents) {
          // If the next component is ClientLogos, pass those as a prop to the content area.
          // If the next component is another Content Area, render it as the next component.
          componentsToRender.push(
            <div key={component.id}>
              <ContentArea
                {...component}
                isSticky
                clientLogos={
                  nextComponent.__typename === "ContentfulClientLogos" &&
                  nextComponent.clientLogo
                }
              />
              {nextComponent.__typename === "ContentfulContentArea" && (
                <ContentArea {...nextComponent} />
              )}
            </div>
          );
          // remove the next ContentArea, so it's not rendered separately
          componentsToNotRender.push(nextComponent.id);
        } else {
          componentsToRender.push(
            <ContentArea key={component.id} {...component} />
          );
        }
      }

      if (component.__typename === "ContentfulJumbotron") {
        componentsToRender.push(
          <Jumbotron key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulImpactStorySummary") {
        componentsToRender.push(
          <ImpactStorySummary key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulCta") {
        componentsToRender.push(<Cta key={component.id} {...component} />);
      }

      if (component.__typename === "ContentfulComponentGroup") {
        componentsToRender.push(
          <ComponentGroup key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulComponentGroupHeading") {
        componentsToRender.push(
          <ComponentGroupHeading key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulParagraph") {
        componentsToRender.push(
          <Paragraph key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulImpactStoryJumbotron") {
        componentsToRender.push(
          <ImpactStoryJumbotron key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulRichTextArea") {
        componentsToRender.push(
          <RichTextArea key={component.id} {...component} />
        );
      }

      if (component.__typename === "ContentfulContactUsForm") {
        componentsToRender.push(
          <ContactUsForm key={component.id} {...component} />
        );
      }
    });

  return componentsToRender.filter(
    (component) => !componentsToNotRender.includes(component.key)
  );
};

export default getContentPageComponents;
